/*
 * Rendering an article.
 *
 * Four steps, in this order, and the order is the security property:
 *
 *   1. Substitute [[kb:...]] tokens for markdown links, using the resolutions the
 *      server computed. The client never decides what a token resolves to - only
 *      the server can see whether a target is published - and it does not decide
 *      what the link's address looks like either: renderArticleLinks builds every
 *      href with the shared articleHref, from the product and slug the server
 *      resolved the token to. A cross-reference can therefore point at another
 *      product's article, which is exactly what the qualified token form is for.
 *   2. Rewrite the destination of every relative image to the endpoint that serves
 *      it for the article being rendered. Absolute http/https destinations pass
 *      through untouched; data:, javascript: and protocol-relative ones are
 *      replaced by their alt text and never become a src at all. All three
 *      decisions are made by classifyArticleImageTarget in packages/shared, so the
 *      editor warnings and this rewrite cannot disagree about what a destination is.
 *   3. marked turns markdown into HTML.
 *   4. DOMPurify removes anything that could execute. Article bodies are written by
 *      administrators, so this is defence in depth rather than the only line - but
 *      an administrator pasting a snippet from somewhere is exactly how stored XSS
 *      arrives, and the cost of sanitising is nil.
 *
 * Heading anchors are stamped after sanitising rather than through a marked
 * renderer override, so [[kb:article#some-heading]] lands in the right place. The
 * ids come from the same toHeadingId in the shared package that the editor heading
 * picker uses, which is what keeps the two ends agreeing.
 *
 * An image whose src fails to load is replaced, after the fact, by a labelled
 * placeholder carrying its alt text. That is the whole reason a missing attachment
 * is still rewritten to a URL that 404s rather than being dropped: a blank gap in
 * the middle of an article is the one failure nobody - reader or author - can see
 * they are looking at.
 *
 * Attachment links (S47, 2026-10-28) join step 1. An [[attachment:<id>]] token
 * becomes a link to the address the PAGE chooses - the reader's article page
 * downloads through the article, the editor preview through the admin API - and
 * clicking one is the page's to handle too (onLinkClick), because a download is a
 * fetch for a short-lived link followed by a navigation, which no href can say on
 * its own. The two token kinds are substituted in ONE back-to-front pass, since
 * both sets of resolutions carry offsets into the text as the server saw it, and
 * substituting either kind first would move the other's.
 *
 * Release notes (S42, 2026-10-12) render through this same component rather than
 * a copy of it, so the security properties above hold for both kinds of body by
 * construction. What differs is said through three props, each defaulting to
 * what an article gets: `videos` (release notes carry none), and `headingOffset`
 * and `headingIdPrefix`, which let the release-note list stack many bodies on one
 * page without their headings outranking the note titles or sharing ids.
 *
 * The two lines this component writes itself - a video that cannot be embedded,
 * an image that did not load - are in the chosen language (S48, 2026-10-29). A
 * change of language renders the body again, so they never lag behind it.
 */

import DOMPurify from 'dompurify';
import { parseVideoReference, renderArticleVideos } from '@accotool-kb/shared';
import { marked } from 'marked';
import { useEffect, useMemo, useRef } from 'react';
import { useMessages } from '../i18n/language.js';
import type { Messages } from '../i18n/en.js';
import {
  renderArticleLinks,
  renderAttachmentLinks,
  rewriteArticleImageSources,
  toHeadingId,
  type ArticleLinkFailure,
  type ArticleLinkResolution,
  type AttachmentLinkResolution,
} from '@accotool-kb/shared';

export interface MarkdownProps {
  content: string;
  /** Token resolutions from the server. Omit to render tokens as plain text. */
  linkTargets?: ArticleLinkResolution[];
  /**
   * [[attachment:...]] resolutions from the server (S47). Rendered only together
   * with `buildAttachmentHref`; without both, the tokens are left as written,
   * which is what a release note - which has no attachments - shows.
   */
  attachmentLinks?: AttachmentLinkResolution[];
  /** Where a resolved attachment link points. */
  buildAttachmentHref?: (attachmentId: string) => string;
  /**
   * Offered every plain left click on a link in the body, with its href as
   * written; returning true takes the click over and cancels the browser's own
   * navigation. A click with a modifier key - a new tab, a new window - is never
   * offered: the browser follows the href, which is why the href must still go
   * somewhere sensible on its own.
   */
  onLinkClick?: (href: string) => boolean;
  /**
   * Builds the src for an image attached to the article being rendered - the
   * public endpoint on the public site, a blob: URL in the editor preview.
   *
   * Omitted, relative image destinations are left exactly as the author wrote
   * them, which resolves against the current page and ends in the placeholder.
   * That is the right answer anywhere an image cannot be resolved at all, and it
   * is not a reason to hide the reference.
   */
  buildImageSrc?: (fileName: string) => string;
  className?: string;
  /**
   * Whether [[video:"..."]] tokens become embeds. Default true, which is what an
   * article gets. False for a release note (S42, 2026-10-12: the owner left
   * video out of release notes): the token is then left as the literal text the
   * author wrote, and a `div.video-embed` placeholder written by hand into the
   * body is neutralised rather than built into a frame - skipping the token pass
   * alone would leave that door open.
   */
  videos?: boolean;
  /**
   * Shifts every rendered heading down by this many levels, clamped at h6 -
   * `##` renders as h4 with an offset of 2. Default 0. For a page that shows a
   * body under headings of its own, such as the release-note list, where each
   * note's title is an h3 and the note's own `##` must nest under it rather
   * than outrank it. Fractions and negatives count as 0.
   */
  headingOffset?: number;
  /**
   * Prepended to every heading id this instance stamps. Default ''. Ids are
   * de-duplicated per instance only, so a page rendering several bodies - the
   * release-note list, where every note may have a "Fixes" heading - needs a
   * prefix per body or the page carries the same id twice. A same-page fragment
   * link inside the body (`[see](#fixes)`) that names one of this body's own
   * headings is re-pointed at the prefixed id, so it still lands.
   */
  headingIdPrefix?: string;
}

const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6';

/** The heading level an offset leaves a heading at: never past h6, never above where it was. */
function shiftedHeadingLevel(level: number, offset: number): number {
  const by = Number.isInteger(offset) && offset > 0 ? offset : 0;
  return Math.min(6, level + by);
}

/*
 * Adds rel and target to links that leave the site.
 *
 * "Leaves the site" is decided on the parsed ORIGIN, not on the text of the href
 * (S42, 2026-10-12 - the owner asked that an external link in a release note
 * always open a new tab, and the rule is the same for an article). The test that
 * stood here was `href.startsWith(window.location.origin)`, a prefix test on a
 * string, and a prefix is not an origin: on https://kb.example.com a link to
 * https://kb.example.com.evil.tld/ or to https://kb.example.com@evil.tld/ began
 * with the page's origin and so stayed in the tab, while a protocol-relative
 * //evil.tld/ link failed the scheme test and stayed in the tab as well.
 * Resolving the href against the page answers all of them the way the browser
 * will when it is clicked: relative and same-origin links stay internal, and any
 * http(s) link to another origin - another host, another port, another scheme -
 * opens a new tab.
 *
 * The scheme gate stays: mailto:, tel: and the like have an opaque origin that
 * never equals the page's, and they must not start opening blank tabs. An href
 * the URL parser refuses is left exactly as it is - throwing here would abort the
 * sanitiser and take the whole body down with one bad link.
 */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (!(node instanceof HTMLElement)) return;
  if (node.tagName !== 'A') return;

  const href = node.getAttribute('href') ?? '';
  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return;
  }
  const isExternal =
    (url.protocol === 'http:' || url.protocol === 'https:') &&
    url.origin !== window.location.origin;

  if (isExternal) {
    node.setAttribute('target', '_blank');
    // noopener is what stops the opened page reaching back through window.opener.
    node.setAttribute('rel', 'noopener noreferrer external');
  }
});

/*
 * Keeps a blob: src on an image, which the default DOMPurify URI allowlist removes.
 *
 * This exists for exactly one caller: the editor preview, which cannot put the
 * admin image endpoint in a src because the whole /api/admin router requires a
 * bearer token and an <img> cannot send a header. It fetches the bytes with the
 * token and hands the element an object URL instead.
 *
 * Allowing it is safe, and not merely convenient. A blob: URL is an opaque handle
 * into THIS document's own memory, minted by our own script; it cannot be authored,
 * guessed, or pointed at anything else, so a blob: src an author typed by hand
 * resolves to nothing and loads nothing. The alternative - restating the default
 * ALLOWED_URI_REGEXP here with one scheme added - would silently fall behind the
 * library the next time they change it.
 *
 * Narrow on purpose: src, on an image, with a blob: value. Nothing else is
 * force-kept, and data: is deliberately still refused - a data: image is an
 * arbitrary payload delivered inside our own origin, which is the entire reason
 * SVG uploads are rejected in the first place.
 */
DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
  if (data.attrName !== 'src') return;
  if (!(node instanceof HTMLElement) || node.tagName !== 'IMG') return;
  if (/^blob:/i.test(data.attrValue)) data.forceKeepAttr = true;
});

/**
 * Both token kinds, substituted back to front in one pass. Each resolution is
 * applied on its own, by its own renderer, to text that has only changed AFTER
 * its offset - which is what keeps every offset true. The renderers check that
 * the token still stands where its offset says, so a resolution list that has
 * drifted from the text leaves a token alone rather than corrupting the body.
 */
function substituteTokens(
  content: string,
  linkTargets: readonly ArticleLinkResolution[],
  attachmentLinks: readonly AttachmentLinkResolution[],
  buildAttachmentHref: ((attachmentId: string) => string) | undefined,
): string {
  const steps: Array<{ index: number; apply: (markdown: string) => string }> = [
    // No options for an article link: the href is not this component's to
    // choose. The alternative - a caller-supplied buildHref, which is what
    // stood here - was rejected on the evidence: every caller passed the same
    // one-segment template literal, and a template literal is how a link
    // quietly stays on an address shape the routes have moved off. The builder
    // lives in the shared package, spelling an article's address once for the
    // routes and the renderer alike. An attachment's address is different in
    // kind - it depends on where the reader may download from - so that one IS
    // the page's.
    ...linkTargets.map((target) => ({
      index: target.index,
      apply: (markdown: string) => renderArticleLinks(markdown, [target]),
    })),
    ...(buildAttachmentHref
      ? attachmentLinks.map((link) => ({
          index: link.index,
          apply: (markdown: string) => renderAttachmentLinks(markdown, [link], { buildHref: buildAttachmentHref }),
        }))
      : []),
  ];
  return steps.sort((a, b) => b.index - a.index).reduce((markdown, step) => step.apply(markdown), content);
}

export function Markdown({
  content,
  linkTargets,
  attachmentLinks,
  buildAttachmentHref,
  onLinkClick,
  buildImageSrc,
  className,
  videos = true,
  headingOffset = 0,
  headingIdPrefix = '',
}: MarkdownProps): JSX.Element {
  const container = useRef<HTMLDivElement>(null);
  const words = useMessages().markdown;

  // Read through a ref, so a page that passes a new handler on every render does
  // not cost a re-render of the whole body.
  const linkClick = useRef(onLinkClick);
  linkClick.current = onLinkClick;

  const html = useMemo(() => {
    const withLinks = substituteTokens(content, linkTargets ?? [], attachmentLinks ?? [], buildAttachmentHref);

    // Only the destination is replaced; alt and title survive byte for byte, so a
    // caption cannot be altered by the act of rendering it.
    const withImages = buildImageSrc
      ? rewriteArticleImageSources(withLinks, { buildSrc: buildImageSrc })
      : withLinks;

    // Video tokens become empty placeholders here and real embeds in the effect
    // below. They cannot become iframes at this stage: the sanitiser forbids
    // iframes outright, and that rule stays exactly as strict as it is. Where
    // videos are off, the token is not touched and reads as the text it is.
    const withVideos = videos ? renderArticleVideos(withImages) : withImages;

    const raw = marked.parse(withVideos, { async: false, gfm: true, breaks: false });

    return DOMPurify.sanitize(typeof raw === 'string' ? raw : '', {
      USE_PROFILES: { html: true },
      // Needed for the external-link handling in the hook above.
      ADD_ATTR: ['target'],
      FORBID_TAGS: ['style', 'form', 'input', 'button', 'iframe', 'object', 'embed'],
      FORBID_ATTR: ['style', 'srcset'],
    });
  }, [content, linkTargets, attachmentLinks, buildAttachmentHref, buildImageSrc, videos]);

  /*
   * One listener for every link in the body, as for image failures: the links
   * arrive as parsed HTML, so there is no per-element moment to attach one. A
   * plain left click only - see onLinkClick.
   */
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const handleClick = (event: MouseEvent): void => {
      const handler = linkClick.current;
      if (!handler || event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target instanceof Element ? event.target.closest('a') : null;
      if (!anchor || !element.contains(anchor)) return;
      const href = anchor.getAttribute('href');
      if (href && handler(href)) event.preventDefault();
    };
    element.addEventListener('click', handleClick);
    return () => element.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    const element = container.current;
    if (!element) return;

    // Attached BEFORE the content exists, and in the capture phase because an
    // image error event does not bubble. One listener on the container rather than
    // one per image: images arrive as parsed HTML, so there is no per-element
    // moment at which to attach anything.
    const onImageError = (event: Event) => handleImageFailure(event, words);
    element.addEventListener('error', onImageError, true);

    element.innerHTML = html;

    // The level shift comes first, so everything after it sees the headings the
    // reader will. Done on the sanitised DOM rather than through a marked renderer
    // override, for the reason the ids are: one place that sees every heading,
    // raw HTML ones included. The element is rebuilt, because a tag name cannot
    // be changed in place; its children move across untouched.
    if (headingOffset !== 0) {
      element.querySelectorAll(HEADING_SELECTOR).forEach((heading) => {
        const level = shiftedHeadingLevel(Number(heading.tagName.slice(1)), headingOffset);
        if (`H${level}` === heading.tagName) return;
        const shifted = document.createElement(`h${level}`);
        for (const attribute of [...heading.attributes]) {
          shifted.setAttribute(attribute.name, attribute.value);
        }
        shifted.append(...heading.childNodes);
        heading.replaceWith(shifted);
      });
    }

    // De-duplicated the same way extractHeadings does, so the nth "Notes" heading
    // gets the same id at both ends. The prefix goes on the front of the finished
    // id, so the de-duplication - and with it the numbering an editor's heading
    // picker offered - is exactly what it is without one.
    const seen = new Map<string, number>();
    const stamped = new Set<string>();
    element.querySelectorAll(HEADING_SELECTOR).forEach((heading) => {
      const base = toHeadingId(heading.textContent ?? '') || 'section';
      const count = seen.get(base) ?? 0;
      seen.set(base, count + 1);
      const id = count === 0 ? base : `${base}-${count}`;
      stamped.add(id);
      heading.id = `${headingIdPrefix}${id}`;
    });

    // A fragment link inside the body that names one of its own headings follows
    // the heading to its prefixed id. Only exact matches move: a fragment aimed at
    // anything else on the page is not this body's to re-point.
    if (headingIdPrefix !== '') {
      element.querySelectorAll('a[href^="#"]').forEach((anchor) => {
        const raw = (anchor.getAttribute('href') ?? '').slice(1);
        let fragment = raw;
        try {
          // marked percent-encodes a non-ASCII fragment; the ids are not encoded.
          fragment = decodeURIComponent(raw);
        } catch {
          // A malformed escape names no heading of ours; leave it alone.
        }
        if (stamped.has(fragment)) anchor.setAttribute('href', `#${headingIdPrefix}${raw}`);
      });
    }

    // Set here rather than in the markdown, so they survive the sanitiser without
    // having to be added to its attribute allowlist. An article that documents a
    // portal is mostly screenshots, and most of them are below the fold.
    element.querySelectorAll('img').forEach((image) => {
      image.loading = 'lazy';
      image.decoding = 'async';
    });

    /*
     * Video embeds, built here rather than in the markdown for the same reason
     * lazy-loading is: an element created in code does not have to be permitted
     * by the sanitiser's allowlist. That is what lets iframe stay in
     * FORBID_TAGS - an author writing a raw <iframe> still gets it stripped,
     * while a [[video:...]] token gets a frame this code built.
     *
     * The uri is re-parsed here, not trusted. The placeholder is ordinary HTML,
     * so an article body could contain one written by hand; re-parsing means
     * such a placeholder is held to the same provider allowlist as a token.
     *
     * Where videos are off, a placeholder can only have been written by hand,
     * and it is disarmed rather than built: the attribute and the class go, so
     * nothing here or in the stylesheet treats it as an embed - not even as an
     * empty 16:9 box - and whatever it wrapped stays as ordinary content.
     */
    if (!videos) {
      element.querySelectorAll('.video-embed').forEach((placeholder) => {
        placeholder.removeAttribute('data-video-uri');
        placeholder.classList.remove('video-embed');
      });
    }
    element.querySelectorAll('div.video-embed[data-video-uri]').forEach((placeholder) => {
      const uri = placeholder.getAttribute('data-video-uri') ?? '';
      // Spent once read: the element is resolved either way below, and leaving
      // a rejected URI lying in the DOM helps nobody read the page.
      placeholder.removeAttribute('data-video-uri');

      const embed = parseVideoReference(uri);
      if (!embed) {
        placeholder.textContent = words.videoNotEmbeddable;
        placeholder.classList.add('video-embed-failed');
        return;
      }

      if (embed.kind === 'file') {
        const video = document.createElement('video');
        video.src = embed.src;
        video.controls = true;
        // Metadata only: an article of clips should not pull every one of them
        // before the reader has asked for any.
        video.preload = 'metadata';
        video.setAttribute('playsinline', '');
        placeholder.replaceChildren(video);
        return;
      }

      // setAttribute throughout rather than the IDL properties: `loading` and
      // `allowFullscreen` are not reflected by every engine (jsdom among them),
      // and an attribute that silently fails to apply is a lazy-loading and
      // fullscreen regression nobody would notice.
      const frame = document.createElement('iframe');
      frame.setAttribute('src', embed.src);
      // An iframe with no accessible name is an unlabelled black box.
      frame.setAttribute('title', embed.title);
      frame.setAttribute('loading', 'lazy');
      frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      // `allow` and nothing else. Chrome warns that it takes precedence over the
      // legacy allowfullscreen attribute, so setting both bought a console
      // warning on every article carrying a video and no behaviour; frameborder
      // is likewise dead, because the stylesheet sets border: 0.
      frame.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen',
      );
      placeholder.replaceChildren(frame);
    });

    element.querySelectorAll('table').forEach((table) => {
      // Wide tables scroll inside their own box rather than making the page scroll.
      if (table.parentElement?.classList.contains('table-scroll')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'table-scroll';
      table.parentElement?.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });

    return () => element.removeEventListener('error', onImageError, true);
  }, [html, videos, headingOffset, headingIdPrefix, words]);

  return <div ref={container} className={className ?? 'markdown'} />;
}

/**
 * Turns an image that did not load into something visible.
 *
 * Every way an image can fail arrives here - the article is not published, the
 * attachment was deleted, the name is misspelt, an external host is down, the
 * object URL behind a preview was revoked - and all of them want the same answer:
 * say so, in place, and keep the alt text where it can be read. The browser own
 * broken-image glyph is not enough; a failed image with alt text collapses to
 * nothing at all in several browsers, and nothing is precisely what an author
 * cannot notice.
 *
 * The element is replaced rather than decorated, because an <img> that failed
 * cannot be made to show its own alt text reliably. That is one-way: nothing
 * retries. Reloading the page is the retry, which is the right granularity for a
 * document.
 */
function handleImageFailure(event: Event, words: Messages['markdown']): void {
  const image = event.target;
  if (!(image instanceof HTMLImageElement)) return;
  // An error can arrive twice for one element; the first has already replaced it.
  if (!image.isConnected) return;

  const alt = (image.getAttribute('alt') ?? '').trim();
  const name = describeFailedSource(image.getAttribute('src'));

  const placeholder = document.createElement('span');
  placeholder.className = 'image-missing';
  placeholder.setAttribute('role', 'img');
  placeholder.setAttribute(
    'aria-label',
    alt.length > 0 ? words.imageNotAvailableAlt(alt) : words.imageNotAvailable,
  );

  const label = document.createElement('span');
  label.className = 'image-missing-label';
  label.textContent = words.imageNotAvailable;
  placeholder.appendChild(label);

  if (name) {
    const source = document.createElement('code');
    source.className = 'image-missing-source';
    source.textContent = name;
    placeholder.appendChild(source);
  }

  if (alt.length > 0) {
    const caption = document.createElement('span');
    caption.className = 'image-missing-alt';
    caption.textContent = alt;
    placeholder.appendChild(caption);
  }

  image.replaceWith(placeholder);
}

/**
 * The part of a failed src worth showing: the file name.
 *
 * A blob: URL is deliberately not shown - its path is a UUID minted by the browser,
 * which tells nobody anything - and neither is a URL that cannot be parsed.
 */
function describeFailedSource(src: string | null): string | null {
  const value = (src ?? '').trim();
  if (value.length === 0) return null;
  if (/^(?:blob|data):/i.test(value)) return null;

  try {
    const segment = new URL(value, window.location.href).pathname.split('/').pop() ?? '';
    const decoded = decodeURIComponent(segment);
    return decoded.length > 0 ? decoded : null;
  } catch {
    return null;
  }
}

/**
 * The editor live preview. Renders the tokens the server could resolve, and marks
 * the ones it could not so the author sees the problem before a reader does.
 *
 * The image audit is deliberately NOT here. It belongs where the author is typing
 * rather than in a pane they may have hidden: a missing alt attribute is not a
 * rendering problem, and it must not go unmentioned because somebody switched the
 * view to Write.
 */
export function MarkdownPreview({
  content,
  linkTargets,
  attachmentLinks = [],
  buildAttachmentHref,
  onLinkClick,
  buildImageSrc,
  videos = true,
}: {
  content: string;
  linkTargets: ArticleLinkResolution[];
  /** Forwarded to Markdown, with the two below (S47). */
  attachmentLinks?: AttachmentLinkResolution[];
  buildAttachmentHref?: (attachmentId: string) => string;
  onLinkClick?: (href: string) => boolean;
  buildImageSrc?: (fileName: string) => string;
  /**
   * Forwarded to Markdown: false previews a body that will be published without
   * video embeds - a release note - exactly as its readers will see it, the
   * token as literal text (S42).
   */
  videos?: boolean;
}): JSX.Element {
  // Its notices are the admin editor's words, t.admin.editor.preview (S48).
  const words = useMessages().admin.editor.preview;
  const unresolved = linkTargets.filter((target) => !target.resolved);
  const notLive = linkTargets.filter((target) => target.resolved && target.targetIsLive === false);
  // Only where attachment links are rendered at all: with no href builder the
  // tokens stay as written, and there is nothing to warn about.
  const missingAttachments = buildAttachmentHref ? attachmentLinks.filter((link) => !link.resolved) : [];

  return (
    <div className="preview">
      {unresolved.length > 0 && (
        <div className="notice notice-warning" role="status">
          <strong>{words.unresolved(unresolved.length)}</strong>
          <ul>
            {unresolved.map((target, index) => (
              <li key={`${target.token}-${index}`}>
                <code>{target.token}</code> — {describeFailure(target.reason, words)}
              </li>
            ))}
          </ul>
          <p>{words.unresolvedBody}</p>
        </div>
      )}

      {notLive.length > 0 && (
        <div className="notice notice-info" role="status">
          <strong>{words.notLive(notLive.length)}</strong>
          <p>{words.notLiveBody}</p>
        </div>
      )}

      {missingAttachments.length > 0 && (
        <div className="notice notice-warning" role="status">
          {/* In .notice-body: a .notice lays its children out in a row. */}
          <div className="notice-body">
            <strong>{words.missingAttachments(missingAttachments.length)}</strong>
            <ul>
              {missingAttachments.map((link, index) => (
                <li key={`${link.token}-${index}`}>
                  <code>{link.token}</code> —{' '}
                  {link.attachmentId ? words.noSuchAttachment : words.notAnAttachmentId}
                </li>
              ))}
            </ul>
            <p style={{ marginBottom: 0 }}>{words.missingAttachmentsBody}</p>
          </div>
        </div>
      )}

      <Markdown
        content={content}
        linkTargets={linkTargets}
        attachmentLinks={attachmentLinks}
        videos={videos}
        {...(buildAttachmentHref ? { buildAttachmentHref } : {})}
        {...(onLinkClick ? { onLinkClick } : {})}
        {...(buildImageSrc ? { buildImageSrc } : {})}
      />
    </div>
  );
}

/**
 * Why a token did not become a link, in the words an author can act on. The three
 * cases call for three different pieces of work, which is the whole reason the
 * shared type keeps them apart: write the target, publish the target, or fix the
 * reference.
 */
function describeFailure(
  reason: ArticleLinkFailure | undefined,
  words: Messages['admin']['editor']['preview'],
): string {
  switch (reason) {
    case 'unwritten':
      return words.linkFailure.unwritten;
    case 'not-published':
      return words.linkFailure.notPublished;
    case 'not-found':
      // Covers the unqualified target too - [[kb:some-slug]] with no product in
      // front of it is half an address, and half an address matches nothing. The
      // author's fix is the same either way: write the reference out in full.
      return words.linkFailure.notFound;
    default:
      return words.linkFailure.other;
  }
}
